import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import LegalLayout from "../components/legal/LegalLayout";

// "Last updated" date for this EULA. Update whenever the copy is revised.
const LAST_UPDATED = "April 20, 2026";

export default function Eula() {
    useEffect(() => {
        document.title = "End User License Agreement — BetweenUs";
    }, []);
    return (
        <LegalLayout
            eyebrow="Legal"
            title="End User License Agreement"
            updatedAt={LAST_UPDATED}
            siblingLabel="Terms & Conditions"
            siblingTo="/terms"
            testIdRoot="eula"
        >
            <section data-testid="eula-intro">
                <h2>Introduction</h2>
                <p>
                    This End User License Agreement (&ldquo;Agreement&rdquo;)
                    is between you (&ldquo;you&rdquo; or &ldquo;User&rdquo;)
                    and <strong>Tylegn Gaming and Entertainment, LLC</strong>,
                    with its principal place of business at{" "}
                    <strong>PO Box 3118, Hailey, ID 83333</strong>{" "}
                    (&ldquo;Tylegn,&rdquo; &ldquo;we,&rdquo; &ldquo;us,&rdquo;
                    or &ldquo;our&rdquo;), regarding your use of the
                    BetweenUs mobile application and any related software,
                    services, and content (collectively, the
                    &ldquo;App&rdquo;).
                </p>
                <p>
                    By downloading, installing, accessing, or using
                    BetweenUs, you agree to this Agreement. If you do not
                    agree, do not download, install, or use the App.
                </p>
            </section>

            <section>
                <h2>1. What BetweenUs Is</h2>
                <p>
                    BetweenUs is designed to help users communicate and
                    share content with people they know or choose to
                    connect with (&ldquo;contacts&rdquo;). Depending on
                    features you enable, the App may allow messaging,
                    sharing of photos and videos, group conversations, and
                    other communication tools intended to support
                    respectful dialogue and conflict resolution.
                </p>
            </section>

            <section>
                <h2>2. License Grant</h2>
                <ul>
                    <li>
                        Tylegn grants you a limited, non‑exclusive,
                        non‑transferable, revocable license to download,
                        install, and use one copy of BetweenUs on a mobile
                        device that you own or control, for your personal,
                        non‑commercial use and in line with this
                        Agreement.
                    </li>
                    <li>
                        This Agreement does not transfer ownership of the
                        App to you. All rights not expressly granted are
                        reserved by Tylegn and its licensors.
                    </li>
                </ul>
            </section>

            <section>
                <h2>3. Eligibility and Accounts</h2>
                <ul>
                    <li>
                        You must be at least <strong>18 years old</strong>,
                        or the age required by law in your jurisdiction,
                        to use BetweenUs. Parents or legal guardians may
                        use the App in structured conversations with their
                        teen, in compliance with applicable law in their
                        region and any relevant consent requirements.
                    </li>
                    <li>
                        If you create an account, you are responsible for
                        your login credentials and all activity under your
                        account.
                    </li>
                    <li>
                        You agree to provide accurate, current, and
                        complete information and to keep it updated.
                    </li>
                </ul>
            </section>

            <section>
                <h2>4. License Restrictions</h2>
                <p>You will not, and will not assist anyone else to:</p>
                <ul>
                    <li>
                        Copy, modify, adapt, translate, or create
                        derivative works of BetweenUs.
                    </li>
                    <li>
                        Reverse engineer, decompile, disassemble, or
                        attempt to derive the source code, except where
                        the law expressly allows.
                    </li>
                    <li>
                        Rent, lease, sell, sublicense, assign, or
                        otherwise transfer the App or any rights under
                        this Agreement.
                    </li>
                    <li>
                        Use BetweenUs for unlawful purposes, harassment,
                        stalking, bullying, or targeted abuse.
                    </li>
                    <li>
                        Bypass or interfere with security or technical
                        features of the App.
                    </li>
                    <li>
                        Use BetweenUs to build a competing product or to
                        scrape data without permission.
                    </li>
                </ul>
            </section>

            <section>
                <h2>5. User Content and Privacy in BetweenUs</h2>
                <p>
                    If BetweenUs allows you to send, post, or share content
                    (messages, voice notes, photos, videos, profile info,
                    etc.) (&ldquo;User Content&rdquo;):
                </p>
                <ul>
                    <li>You retain ownership of your User Content.</li>
                    <li>
                        You grant Tylegn a worldwide, non‑exclusive,
                        royalty‑free, transferable, sublicensable license
                        to use, host, store, reproduce, modify, adapt,
                        publish, translate, create derivative works from,
                        distribute, publicly perform, and publicly display
                        your User Content as needed to operate, improve,
                        and secure BetweenUs and related services.
                    </li>
                    <li>
                        You represent that you have all rights necessary
                        to share User Content and that it does not:
                    </li>
                </ul>
                <ul>
                    <li>
                        Infringe any third‑party intellectual property or
                        other rights.
                    </li>
                    <li>
                        Violate privacy, publicity, or confidentiality
                        rights of others.
                    </li>
                    <li>
                        Contain unlawful, harassing, hateful, abusive, or
                        otherwise prohibited material.
                    </li>
                </ul>
                <p>
                    Because BetweenUs is about communication between
                    people you know, you agree not to use the App to:
                </p>
                <ul>
                    <li>
                        Share private or sensitive information about
                        others without their consent (including contacts
                        lists, private messages, or intimate content).
                    </li>
                    <li>
                        Impersonate anyone or misrepresent your identity
                        or affiliation.
                    </li>
                    <li>
                        Spam, bulk message, or harass other users.
                    </li>
                </ul>
                <p>
                    Tylegn may, but is not required to, review, remove, or
                    restrict access to User Content if it violates this
                    Agreement or the law.
                </p>
            </section>

            <section>
                <h2>6. Community Standards</h2>
                <p>
                    You agree to use BetweenUs for respectful, consensual
                    communication. You will not use the App to:
                </p>
                <ul>
                    <li>
                        Harass, threaten, stalk, or bully any person.
                    </li>
                    <li>
                        Promote hate, violence, or discrimination based on
                        protected characteristics.
                    </li>
                    <li>
                        Share non‑consensual intimate images or private
                        information about others.
                    </li>
                    <li>
                        Facilitate illegal activity, including scams,
                        fraud, or exploitation.
                    </li>
                </ul>
                <p>
                    Tylegn may suspend or terminate your access to
                    BetweenUs if your conduct violates these standards or
                    creates harm or legal risk for others or for Tylegn.
                </p>
            </section>

            <section>
                <h2>7. Updates</h2>
                <ul>
                    <li>
                        Tylegn may provide updates, upgrades, or new
                        versions (&ldquo;Updates&rdquo;) to BetweenUs.
                    </li>
                    <li>
                        Updates may install automatically or may require
                        manual installation, depending on your device and
                        app store settings.
                    </li>
                    <li>
                        This Agreement applies to any Updates unless an
                        Update comes with a separate agreement.
                    </li>
                </ul>
            </section>

            <section>
                <h2>8. Third‑Party Services</h2>
                <p>
                    BetweenUs may include links or integrations with
                    third‑party services (e.g., cloud storage, analytics,
                    payment processors, AI platforms). Tylegn does not own
                    or control these services and is not responsible for
                    them. Your use may be subject to separate terms and
                    privacy policies.
                </p>
            </section>

            <section>
                <h2>9. Data and Privacy</h2>
                <ul>
                    <li>
                        Tylegn may collect and process information about
                        your use of BetweenUs, including technical data
                        and, where applicable, personal data (including
                        messages and contact information you choose to
                        share).
                    </li>
                    <li>
                        Our practices are described in Tylegn&rsquo;s{" "}
                        <Link to="/privacy" data-testid="eula-link-privacy">
                            Privacy Policy
                        </Link>
                        , incorporated here by reference. By using
                        BetweenUs, you consent to our data practices as
                        described there.
                    </li>
                </ul>
            </section>

            <section>
                <h2>10. Fees and Purchases</h2>
                <p>
                    If BetweenUs includes optional paid features (such as
                    premium templates, extra history, or additional
                    exports):
                </p>
                <ul>
                    <li>
                        Prices, billing cycles, and renewal terms are
                        shown at purchase or in your account.
                    </li>
                    <li>
                        Payments are processed by the app store or a
                        payment provider; their terms also apply.
                    </li>
                    <li>
                        Except where required by law or the app store,
                        payments are non‑refundable.
                    </li>
                </ul>
                <p>
                    The App is currently provided free of charge, but
                    Tylegn reserves the right to introduce paid features
                    in the future. Any such features will be clearly
                    disclosed and optional.
                </p>
            </section>

            <section>
                <h2>11. Intellectual Property</h2>
                <ul>
                    <li>
                        BetweenUs and all its components are owned by
                        Tylegn or its licensors and protected by
                        intellectual property laws.
                    </li>
                    <li>
                        Nothing in this Agreement gives you ownership
                        rights in the App.
                    </li>
                    <li>
                        Do not remove or alter any copyright, trademark,
                        or other notices.
                    </li>
                </ul>
            </section>

            <section>
                <h2>12. Term and Termination</h2>
                <ul>
                    <li>
                        This Agreement starts when you first use BetweenUs
                        and continues until terminated by you or Tylegn.
                    </li>
                    <li>
                        You may terminate by deleting the App and, if
                        applicable, closing your account.
                    </li>
                    <li>
                        Tylegn may suspend or terminate your access at any
                        time if you breach this Agreement, break the law,
                        or create risk for others or for Tylegn.
                    </li>
                    <li>
                        Upon termination, your license ends and you must
                        stop using and delete the App.
                    </li>
                </ul>
                <p>
                    Sections that reasonably should survive (IP,
                    disclaimers, liability limits, dispute resolution)
                    continue to apply.
                </p>
            </section>

            <section>
                <h2>13. Disclaimer of Warranties</h2>
                <ul>
                    <li>
                        BetweenUs is provided &ldquo;as is&rdquo; and
                        &ldquo;as available&rdquo; without warranties of
                        any kind, express or implied.
                    </li>
                    <li>
                        Tylegn disclaims implied warranties of
                        merchantability, fitness for a particular purpose,
                        and non‑infringement.
                    </li>
                    <li>
                        Tylegn does not guarantee uninterrupted, secure,
                        or error‑free operation.
                    </li>
                </ul>
                <p>
                    Some jurisdictions do not allow these disclaimers, so
                    they may not fully apply to you.
                </p>
            </section>

            <section>
                <h2>14. Limitation of Liability</h2>
                <p>To the fullest extent allowed by law:</p>
                <ul>
                    <li>
                        Tylegn is not liable for indirect, incidental,
                        consequential, special, or punitive damages, or
                        for loss of profits, data, or goodwill arising
                        from your use of BetweenUs.
                    </li>
                    <li>
                        Tylegn&rsquo;s total liability is limited to the
                        greater of:
                    </li>
                </ul>
                <ul>
                    <li>
                        The amount you paid (if any) in the 12 months
                        before the claim, or
                    </li>
                    <li>
                        <strong>USD $50</strong>.
                    </li>
                </ul>
                <p>Some jurisdictions do not allow these limitations.</p>
            </section>

            <section>
                <h2>15. Indemnification</h2>
                <p>
                    You agree to indemnify and hold harmless Tylegn and
                    its officers, directors, employees, agents, and
                    affiliates from claims, damages, losses, costs, and
                    expenses (including reasonable attorneys&rsquo; fees)
                    arising from:
                </p>
                <ul>
                    <li>Your use of BetweenUs.</li>
                    <li>
                        Your violation of this Agreement or the law.
                    </li>
                    <li>
                        Your User Content or interactions with other
                        users.
                    </li>
                </ul>
            </section>

            <section>
                <h2>16. App Store Terms</h2>
                <p>
                    If you download BetweenUs from an app store (Apple App
                    Store, Google Play):
                </p>
                <ul>
                    <li>
                        This Agreement is between you and Tylegn, not the
                        app store.
                    </li>
                    <li>
                        The app store is not responsible for the App or
                        its content.
                    </li>
                    <li>
                        The app store may be a third‑party beneficiary
                        with the right to enforce this Agreement against
                        you where permitted.
                    </li>
                    <li>
                        You also agree to comply with applicable app store
                        terms and policies.
                    </li>
                </ul>
            </section>

            <section>
                <h2>17. Governing Law and Disputes</h2>
                <p>
                    This Agreement is governed by the laws of the State of{" "}
                    <strong>Idaho, United States</strong>, where Tylegn
                    Gaming and Entertainment, LLC is organized, without
                    regard to its conflict‑of‑laws rules. If you are a
                    consumer located outside the United States, the
                    mandatory consumer‑protection laws of your country of
                    residence will continue to apply to you where they are
                    required and cannot be waived by contract.
                </p>
                <p>
                    Any dispute, claim, or controversy arising out of or
                    relating to this Agreement will be resolved{" "}
                    <strong>as described in our Terms and Conditions</strong>
                    {" "}(see{" "}
                    <Link
                        to="/terms"
                        data-testid="eula-link-terms-disputes"
                    >
                        Terms &sect; 14 &mdash; Governing Law, Disputes &amp;
                        Arbitration
                    </Link>
                    ), including informal resolution first, binding
                    individual arbitration under the American Arbitration
                    Association&rsquo;s Consumer Arbitration Rules (held
                    remotely by default so users around the world are not
                    required to travel), a class‑action waiver with
                    small‑claims and intellectual‑property carve‑outs, and
                    a 30‑day opt‑out. Nothing in this Section limits any
                    non‑waivable rights you may have as a consumer under
                    the laws of your country or region of residence.
                </p>
            </section>

            <section>
                <h2>18. Changes to This Agreement</h2>
                <ul>
                    <li>
                        Tylegn may update this Agreement periodically.
                    </li>
                    <li>
                        For material changes, we will provide notice
                        through the App, our website, or other reasonable
                        means.
                    </li>
                    <li>
                        Continuing to use BetweenUs after changes take
                        effect means you accept the updated Agreement. If
                        you do not agree, stop using and delete the App.
                    </li>
                </ul>
            </section>

            <section>
                <h2>19. Contact Information</h2>
                <p>
                    Questions about this Agreement or BetweenUs? Contact:
                </p>
                <address>
                    <strong>Tylegn Gaming and Entertainment, LLC</strong>
                    <br />
                    Email:{" "}
                    <a href="mailto:support@tylegn.com">
                        support@tylegn.com
                    </a>
                    <br />
                    Mailing Address: PO Box 3118, Hailey, ID 83333
                </address>
            </section>

            <p className="legal-cross-link">
                See also:{" "}
                <Link to="/terms" data-testid="eula-link-terms">
                    Terms &amp; Conditions
                </Link>
                {" "}·{" "}
                <Link to="/privacy" data-testid="eula-link-privacy-bottom">
                    Privacy Policy
                </Link>
                .
            </p>
        </LegalLayout>
    );
}
